import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import * as lunr from 'lunr';
import { Observable } from 'rxjs';

import { AppCluster } from '../appkey';
import { Endpoint, EndpointService } from '../endpoints';
import { IApiDocumentationServiceData, ISearchData } from './api-documentation.models';

@Injectable({
	providedIn: 'root'
})
export class ApiDocumentationService extends OnDestroyMixin {
	data: IApiDocumentationServiceData = {
		apiDocumentation: {},
		search: null
	};

	constructor(private endpointService: EndpointService, private http: HttpClient) {
		super();
	}

	search(query: string) {
		if (!this.data.search) {
			return [];
		}

		if (!query.trim()) {
			return (this.data.search.resultDocuments = []);
		}

		this.data.search.resultDocuments = [];
		const results = this.data.search.index.search(query);

		for (let i = 0; i < results.length; i++) {
			const result = results[i];
			const doc = this.data.search.documents[result.ref];
			doc.summary = doc.text.substring(0, 200);
			this.data.search.resultDocuments.push(doc);
		}

		return this.data.search.resultDocuments;
	}

	getApiDocumentation(fileName: string = 'index.html', force = false): Observable<any> {
		const url = `${this.endpointService.url(Endpoint.apiDocumentation)}?documentation-url=${encodeURIComponent(
			`/${fileName}`
		)}`;

		const options = {
			headers: new HttpHeaders({
				Accept: 'text/html'
			}),
			responseType: 'text'
		} as any;

		return new Observable<any>((observer) => {
			if (force || !this.data.apiDocumentation[`/${fileName}`]) {
				this.http.get(url, options).subscribe(
					(res: any) => {
						this.data.apiDocumentation[`/${fileName}`] = res;
						observer.next(res);
						observer.complete();
					},
					(err: any) => {
						observer.error(err);
						observer.complete();
					}
				);
			} else {
				observer.next(this.data.apiDocumentation[`/${fileName}`]);
				observer.complete();
			}
		});
	}

	getSearchIndex(force = false): Observable<any> {
		const url = `${this.endpointService.url(Endpoint.apiDocumentation)}?documentation-url=${encodeURIComponent(
			`/search/search_index.json`
		)}`;

		const options = {
			headers: new HttpHeaders({
				Accept: 'application/json'
			}),
			responseType: 'text'
		} as any;

		return new Observable<any>((observer) => {
			if (force || !this.data.search) {
				this.http.get(url, options).subscribe(
					(res: any) => {
						this.data.search = {
							resultDocuments: [],
							documents: {},
							index: {}
						};
						this.initSearch(JSON.parse(res));
						observer.next(res);
						observer.complete();
					},
					(err: any) => {
						observer.error(err);
						observer.complete();
					}
				);
			} else {
				observer.next(this.data.search.index);
				observer.complete();
			}
		});
	}

	private initSearch(data: ISearchData) {
		const documents = {};
		const lang = ['en'];

		if (data.config && data.config.separator && data.config.separator.length) {
			lunr.tokenizer.separator = new RegExp(/[\s\-\/{}()]+/);
		}

		if (data.index) {
			this.data.search.index = lunr.Index.load(data.index);
			data.docs.forEach((doc) => {
				documents[doc.location] = doc;
			});
		} else {
			this.data.search.index = lunr(function () {
				// Testing removing stemming since it behaves wonky in autocomplete scenario, "acti" finding
				// results but "activi" does not because stemm word doesnt exist. This doesn't actually seem
				// to make much difference. Think its because of the way we build the search data and the tokens
				// created when doing it. Would need to modify the search index creation to really work around
				// this issue if its a problem going forward.
				this.pipeline.remove(lunr.stemmer);
				this.searchPipeline.remove(lunr.stemmer);

				if (lang.length === 1 && lang[0] !== 'en' && lunr[lang[0]]) {
					this.use(lunr[lang[0]]);
				} else if (lang.length > 1) {
					// Don't support multiple languages, need to investigate what additional scripts needs to be loaded
					// https://github.com/mkdocs/mkdocs/blob/ff0b7260564e65b6547fd41753ec971e4237823b/mkdocs/contrib/search/templates/search/worker.js#L36
					// this.use(lunr.multiLanguage.apply(null, lang));
				}
				this.field('title');
				this.field('text');
				this.ref('location');

				for (let i = 0; i < data.docs.length; i++) {
					const doc = data.docs[i];
					this.add(doc);
					documents[doc.location] = doc;
				}
			});
		}

		this.data.search.documents = documents;
	}
}
